import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useState } from 'react';
import {
  AutocompleteInput,
  Button,
  Confirm,
  Labeled,
  ReferenceInput,
  SaveButton,
  SelectInput,
  TextField,
  TextInput,
  Toolbar,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';
import type { FilterSpec } from 'ra-postgraphile';
import { FormHeader } from 'src/client/components/forms/form-components';

export type AdvertiserRecord = {
  id: string;
  name: string;
  type: AdvertiserType;
  parentId?: string | null;
  externalPioId?: string | null;
  adServerId?: string | null;
  archivedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AdvertiserType = 'ADVERTISER' | 'AGENCY' | 'BRAND' | 'PARTNER' | 'HOLDING_COMPANY';

export const advertiserTypeChoices: { id: AdvertiserType; name: string }[] = [
  { id: 'ADVERTISER', name: 'Advertiser' },
  { id: 'AGENCY', name: 'Agency' },
  { id: 'BRAND', name: 'Brand' },
  { id: 'PARTNER', name: 'Partner' },
  { id: 'HOLDING_COMPANY', name: 'Holding Company' },
];

export const activeAdvertiserFilter: FilterSpec = { operator: 'isNull', value: true };
export const archivedAdvertiserFilter: FilterSpec = { operator: 'isNull', value: false };

export const formatArchivedAtFilter = (value?: FilterSpec) => value?.operator === 'isNull' && value.value === false;

export const parseArchivedAtFilter = (value: boolean): FilterSpec =>
  value ? archivedAdvertiserFilter : activeAdvertiserFilter;

const nullableString = (value?: string | null) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const transformAdvertiserInput = (data: Partial<AdvertiserRecord>) => ({
  name: data.name,
  type: data.type,
  parentId: data.parentId || null,
  adServerId: nullableString(data.adServerId),
  archivedAt: data.archivedAt ?? null,
});

const agencyFilter = (recordId?: string) => ({
  archivedAt: activeAdvertiserFilter,
  type: 'AGENCY',
  id: { operator: 'notEqualTo', value: recordId },
});

const AgencyInput = () => {
  const record = useRecordContext<AdvertiserRecord>();

  return (
    <ReferenceInput
      reference="advertisers"
      source="parentId"
      filter={agencyFilter(record?.id)}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={50}
    >
      <AutocompleteInput
        label="Agency"
        optionText="name"
        filterToQuery={searchText => ({ name: { operator: 'includesInsensitive', value: searchText } })}
        shouldRenderSuggestions={() => true}
        noOptionsText="No active agencies found"
      />
    </ReferenceInput>
  );
};

export const AdvertiserFormFields = ({
  title,
  showAdServerId = true,
  showExternalPioId = false,
}: {
  title: string;
  showAdServerId?: boolean;
  showExternalPioId?: boolean;
}) => (
  <>
    <FormHeader>{title}</FormHeader>
    <TextInput source="name" label="Name" resettable fullWidth validate={required()} />
    <SelectInput source="type" label="Type" choices={advertiserTypeChoices} validate={required()} />
    <AgencyInput />
    {showAdServerId && <TextInput source="adServerId" label="GAM/DCM Company ID" resettable />}
    {showExternalPioId && (
      <Labeled source="externalPioId">
        <TextField source="externalPioId" emptyText="-" />
      </Labeled>
    )}
  </>
);

export const ArchiveAdvertiserButton = () => {
  const record = useRecordContext<AdvertiserRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();
  const [isOpen, setIsOpen] = useState(false);

  if (!record) return null;

  const isArchived = Boolean(record.archivedAt);
  const action = isArchived ? 'Restore' : 'Archive';

  const handleConfirm = () => {
    update(
      'advertisers',
      {
        id: record.id,
        data: {
          archivedAt: isArchived ? null : new Date().toISOString(),
        },
        previousData: record,
      },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(`Advertiser ${isArchived ? 'restored' : 'archived'}`);
          refresh();
        },
        onError: error => {
          notify(`Error: ${error}`, { type: 'warning' });
        },
      },
    );
    setIsOpen(false);
  };

  return (
    <>
      <Button
        label={action}
        onClick={event => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        disabled={isLoading}
        color={isArchived ? 'info' : 'warning'}
      >
        {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={isLoading}
        title={`${action} advertiser`}
        content={`Are you sure you want to ${action.toLowerCase()} ${record.name}?`}
        onConfirm={handleConfirm}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export const AdvertiserEditToolbar = () => (
  <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <SaveButton />
    <ArchiveAdvertiserButton />
  </Toolbar>
);
